import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { captureGrowthTouch, consumeGrowthEvents, readGrowthTouch } from '@/lib/growth-attribution';
import { useConfig } from '@/hooks/useConfig';
import { useSessionQuery } from '@/hooks/useSession';
import { identify, initAnalytics, isAnalyticsReady, reset, track } from '@/lib/analytics';

/**
 * Public marketing pages that fire `landing_viewed`, mapped to their copy
 * variant ('' = the control). /landing-test is the copy-experiment slot
 * (features/marketing/DuckingHero.tsx); it is harmless while unrouted.
 */
const LANDING_PAGES: Record<string, string> = {
  '/landing': '',
  '/landing-test': 'landing-test',
  '/download': '',
  '/mobile': '',
  '/demo': ''
};

/**
 * Wires PostHog to the router and the session. Sits outside the auth gate, so
 * it reads the raw session query rather than `useSession()`; a 401 while
 * logged out is normal and simply means "nobody to identify".
 *
 * Renders nothing. With `config.posthog === null` (dev, alpha) it never
 * initializes, so every call below is a no-op and no network request is made.
 */
export function Analytics() {
  const config = useConfig();
  const session = useSessionQuery();
  const location = useLocation();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!config.data) return;
    initAnalytics(config.data);
    if (isAnalyticsReady()) setReady(true);
  }, [config.data]);

  useEffect(() => {
    if (!ready) return;
    // The static resource pages (marketing/) load no analytics; they leave a
    // campaign touch and a few clicks in localStorage, replayed here on the
    // visitor's first app pageview. See lib/growth-attribution.ts.
    captureGrowthTouch();
    const touch = readGrowthTouch();
    track('$pageview', touch ? { growth: touch } : undefined);
    // Funnel step 1: a view of a public marketing page. Fired here, not in the
    // page, because the page can render before PostHog is initialized.
    const landing = LANDING_PAGES[location.pathname];
    if (landing !== undefined)
      track('landing_viewed', { path: location.pathname, ...(landing ? { variant: landing } : {}), growth: touch });
    for (const entry of consumeGrowthEvents()) {
      // Funnel step 2: CTA clicks, queued at click time because every CTA
      // navigates away (lib/growth-attribution.ts recordCtaClick).
      if (entry.cta)
        track(
          'cta_clicked',
          { cta: entry.cta, location: entry.location, destination: entry.destination, path: entry.path, growth: touch },
          { timestamp: new Date(entry.occurred_at) }
        );
      if (entry.event !== 'cta_clicked')
        track(`growth_${entry.event}`, { ...entry, growth: touch, delayed_until_app_visit: true });
    }
  }, [ready, location.pathname, location.search]);

  const userId = session.data?.user.id || null;
  const userName = session.data?.user.name ?? null;
  const userEmail = session.data?.user.email ?? null;
  const signedOut = session.isError || (session.isSuccess && !userId);
  const lastUserId = useRef<string | null>(null);

  useEffect(() => {
    if (!ready) return;
    if (userId) {
      if (lastUserId.current !== userId) {
        identify(userId, { name: userName, email: userEmail });
        const touch = readGrowthTouch();
        if (touch) track('growth_touch_identified', { ...touch, attribution_model: 'last_campaign_or_first_public_visit_30d' });
        lastUserId.current = userId;
      }
    } else if (signedOut && lastUserId.current) {
      reset();
      lastUserId.current = null;
    }
  }, [ready, userId, userName, userEmail, signedOut]);

  return null;
}
